import { useCallback, useEffect, useState } from "react";
import { IoTrashOutline } from "react-icons/io5";
import {
  deleteMedia,
  listMedia,
  updateMedia,
  uploadMedia,
} from "../../../api/mediaApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import DashboardPanel from "../../../components/dashboard/DashboardPanel";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import MediaPreview from "./MediaPreview";
import MediaUploadBox from "./MediaUploadBox";

const GROUP_OPTIONS = ["background", "main", "slides", "gallery"];

export default function MediaManager({ sectionId, sectionType }) {
  const notify = useNotify();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [groupKey, setGroupKey] = useState("main");

  const loadMedia = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listMedia(sectionId);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  useEffect(() => {
    if (sectionType === "hero") setGroupKey("background");
    else if (sectionType === "slider") setGroupKey("slides");
    else if (sectionType === "gallery") setGroupKey("gallery");
    else setGroupKey("main");
  }, [sectionType]);

  async function handleUpload(file, mediaType) {
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mediaType", mediaType);
      form.append("groupKey", groupKey);
      form.append("altText", file.name);
      form.append("isPrimary", items.length === 0 ? "true" : "false");
      form.append("sortOrder", String(items.length + 1));

      await uploadMedia(sectionId, form);
      notify.success("Media uploaded");
      await loadMedia();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setUploadBusy(false);
    }
  }

  async function handleSetPrimary(item) {
    try {
      await updateMedia(item.id, {
        mediaType: item.mediaType,
        url: item.url,
        altText: item.altText,
        groupKey: item.groupKey,
        sortOrder: item.sortOrder,
        isPrimary: true,
        isPublished: item.isPublished !== false,
      });
      notify.success("Primary media updated");
      await loadMedia();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMedia(deleteTarget.id);
      notify.success("Media deleted");
      setDeleteTarget(null);
      await loadMedia();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    }
  }

  if (loading) return <LoadingState message="Loading media…" />;
  if (error) return <ErrorState message={error} onRetry={loadMedia} />;

  return (
    <div className="space-y-5">
      <DashboardPanel
        title="Upload media"
        description="Choose a group, then upload images or videos for this section"
        bodyClassName="p-4"
      >
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm">
            <span className="font-medium text-slate-700">Group</span>
            <select
              value={groupKey}
              onChange={(e) => setGroupKey(e.target.value)}
              className="mt-2 block rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            >
              {GROUP_OPTIONS.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
          </label>
          <div className="min-w-[12rem] flex-1">
            <MediaUploadBox
              mediaType={sectionType === "video" ? "video" : "image"}
              busy={uploadBusy}
              onUpload={handleUpload}
            />
          </div>
        </div>
      </DashboardPanel>

      {items.length === 0 ? (
        <EmptyState
          title="No media yet"
          description="Upload images or videos for this section."
        />
      ) : (
        <DashboardPanel
          title="Uploaded files"
          description={`${items.length} file${items.length === 1 ? "" : "s"} in this section`}
          bodyClassName="p-4"
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="rounded-xl border border-slate-200 bg-slate-50/50 p-3"
              >
                <MediaPreview item={item} />
                <div className="mt-3 space-y-1 text-xs text-slate-500">
                  <p className="font-medium capitalize">{item.groupKey || "main"}</p>
                  {item.isPrimary ? (
                    <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                      Primary
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSetPrimary(item)}
                      className="text-slate-600 underline"
                    >
                      Set as primary
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(item)}
                  className="mt-3 inline-flex items-center gap-1 text-sm text-red-600"
                >
                  <IoTrashOutline className="h-4 w-4" />
                  Delete
                </button>
              </div>
            ))}
          </div>
        </DashboardPanel>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete media?"
        message="This media file will be removed from the section."
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
